import React, { ReactElement } from 'react'
import { Flex, Text, Box } from 'theme-ui'
import CheckIcon from 'images/organization/homepage/sections/solutions/check-all.svg'
import { Svg } from 'ui'
import SolutionContainer from './SolutionContainer'
import { solutionsData } from './solutionsData'
import BlurIcon from 'images/organization/homepage/sections/solutions/solutions-blur.svg'
import StarsWrapper, { bigStarStyles } from 'components/Organization/HomePage/StarsWrapper'
import LogoBgIcon from 'images/organization/homepage/sections/solutions/logo-bg.svg'

export default function Solutions(): ReactElement {
  return (
    <Flex
      id="solutions-section"
      className="ios-scrolling-glitches-fix"
      sx={{
        position: 'relative',
        width: '100%',
        justifyContent: 'center',
        py: ['6.4rem', '10rem', '10rem'],
        fontSize: '2.1rem',
        px: ['1.6rem', '1.6rem', 0],
        overflow: 'hidden',
        borderWidth: '0.1rem',
        borderColor: 'wakaiwaGrey20',
        borderTopStyle: ['solid', 'none'],
        borderBottomStyle: ['solid', 'none'],
        backgroundColor: 'blue800',
        zIndex: 'zIndex5',
      }}
    >
      <Flex
        className="ios-scrolling-glitches-fix"
        sx={{
          position: 'absolute',
          width: '100%',
          height: ['39rem', '32rem'],
          top: ['14.4rem', '26rem'],
          background: 'hotPink10',
          filter: ['blur(75px)', 'blur(150px)'],
        }}
      />
      <Flex
        sx={{
          position: 'relative',
          width: '100%',
          justifyContent: 'center',
        }}
      >
        <Svg
          svg={LogoBgIcon}
          sx={{
            visibility: ['hidden', 'hidden', 'visible'],
            position: 'absolute',
            top: '-50rem',
            right: 0,
          }}
          descriptiveText="Decorative logo background for solutions section"
        />
        <Flex
          sx={{
            width: '100%',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <StarsWrapper
            sx={{ width: ['100%', 'auto'] }}
            starsSx={[
              {
                top: '8rem',
                left: '17rem',
              },
              {
                top: '32rem',
                left: '-14rem',
              },
              {
                top: '21rem',
                left: '88.5rem',
                ...bigStarStyles,
              },
            ]}
          >
            <Flex
              sx={{
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                width: '100%',
                height: 'fit-content',
              }}
            >
              <Flex
                sx={{
                  position: 'relative',
                  width: '8rem',
                  height: '8rem',
                  py: 0,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Flex
                  sx={{
                    position: 'absolute',
                    borderRadius: '50%',
                    width: '7.8rem',
                    height: '7.8rem',
                    justifyContent: 'center',
                    alignItems: 'center',
                    background:
                      'linear-gradient(180deg, rgba(255, 118, 174, 0.1) 0%, rgba(255, 118, 174, 0.00) 100%)',
                  }}
                />
                <svg
                  width="80"
                  height="80"
                  style={{
                    position: 'absolute',
                    top: 0,
                    transform: 'rotate(30deg)',
                    WebkitTransform: 'translateZ(0)',
                    contentVisibility: 'auto',
                  }}
                >
                  <linearGradient id="linearColors" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="rgba(255, 118, 174, 0.20)"></stop>
                    <stop offset="100%" stopColor="rgba(255, 118, 174, 0.10)"></stop>
                  </linearGradient>
                  <circle
                    r="38"
                    cx="40"
                    cy="39"
                    className="external-circle"
                    strokeWidth="1"
                    fill="none"
                    stroke="url(#linearColors)"
                  ></circle>
                </svg>
                <Svg
                  svg={BlurIcon}
                  sx={{ position: 'absolute', top: '-50%' }}
                  descriptiveText="Blur effect for solutions icon"
                />
                <Svg
                  svg={CheckIcon}
                  sx={{
                    width: ['4rem', '4.8rem'],
                    height: ['4rem', '4.8rem'],
                  }}
                  descriptiveText="Check icon for solutions section"
                />
              </Flex>
              <Text
                as="h2"
                sx={{
                  color: 'aliceBlue',
                  fontSize: ['2.8rem', '2.8rem', '4rem'],
                  fontWeight: 600,
                  mt: '1.6rem',
                }}
              >
                Solutions
              </Text>
              <Text
                sx={{
                  position: 'relative',
                  width: ['100%', '48rem'],
                  color: 'lightSteelBlue',
                  fontSize: ['1.5rem', '1.5rem', '1.6rem'],
                  mt: ['0.8rem', '0.8rem', 0],
                }}
              >
                You have problems, we have solutions that won&apos;t break the bank, and won&apos;t
                require any long term commitments
                <Box
                  className="ios-scrolling-glitches-fix"
                  sx={{
                    display: ['block', 'block', 'none'],
                    position: 'absolute',
                    top: '-5rem',
                    maxWidth: '39rem',
                    width: '100%',
                    height: '10rem',
                    backgroundColor: 'rgba(102, 255, 255, 0.15)',
                    filter: 'blur(75px)',
                  }}
                />
              </Text>
              <Flex
                sx={{
                  width: ['100%', 'auto'],
                  flexDirection: 'column',
                  mt: ['5.8rem', '8rem'],
                  gap: '3.2rem',
                  '.solution-container:nth-of-type(odd)>div': {
                    flexDirection: ['column', 'row'],
                  },
                  '.solution-container:nth-of-type(even)>div': {
                    flexDirection: ['column', 'row-reverse'],
                  },
                }}
              >
                {solutionsData.map(({ title, subText, features, url, illustration, type }) => (
                  <SolutionContainer
                    key={title}
                    title={title}
                    subText={subText}
                    features={features}
                    url={url}
                    illustration={illustration}
                    type={type}
                  />
                ))}
              </Flex>
            </Flex>
          </StarsWrapper>
        </Flex>
      </Flex>
    </Flex>
  )
}
